import { createAction } from 'typesafe-actions';
import { LevelStatus, LevelType } from '../Level/ILevelState';
import { IUserState } from '../User/IUserState';
import { IPlayerState } from '../Player/IPlayerState';
import { ContextType } from './IContext';
import { IPlatformState } from './IPlatformState';
import { ProductID } from '../Player/Product';
import { EntryPoint } from './EntryPoint/GameEntryPoint';

export enum Types {
    ENABLE_IAP = 'enableIAP',
    READY_IAP = 'readyIAP',
    SET_SCENE_LOADED = 'setSceneLoaded',
    SET_SCENE_LOADING = 'setSceneLoading',
    SET_LOADED_DATA = 'setLoadedData',
    GAME_LOADED = 'gameLoaded',
    LOAD_WORLD = 'loadWorld',
    LOAD_LEVEL = 'loadLevel',
    RESTART_LEVEL = 'restartLevel',
    SET_CONTEXT = 'setContext',
    RESTORE_DEFAULT_CONTEXT = 'restoreDefaultContext',
    END_LEVEL = 'endLevel',
}

export enum ResponseType {
    PURCHASE_PRODUCT = 'purchaseProduct',
    PLAY_WITH_FRIENDS = 'playWithFriends',
    INVITE_FRIEND = 'inviteFriend',
    SHARE_BEAT_HIGHSCORE_LEVEL = 'shareBeatHighscoreLevel',
    SEND_LIFE = 'sendLife',
    REQUEST_LIFE = 'requestLife',
    SEND_GIFT = 'sendGift',
    SHARE_3_STAR_REWARD = 'share3StarReward',
    SHARE_POSTCARD = 'sharePostcard',
    SHARE_SCREENSHOT = 'shareScreenshot',
    DISPLAY_ADVERTISEMENT = 'displayAdvertisement',
    SUBSCRIBE_BOT_OPT_IN = 'subscribeBotOptIn',
    CREATE_SHORTCUT = 'createShortcut',
    SWITCH_GAME = 'switchGame',
    REQUEST_HELP = 'requestHelp',
}

export enum ResponseStatus {
    SUCCESS = 'success',
    FAIL = 'fail',
}

export interface ILoadWorldPayload {
    worldNumber: number;

    /**
     * Temporary fix for UI that needs to activate after the world loads.
     */
    clearUI: boolean;
}

export interface ILoadLevelPayload {
    levelNumber: number;
    levelPath: string;
    levelType: LevelType;
}

export interface IGameLoadData {
    userState: Partial<IUserState>;
    platform: Partial<IPlatformState>;
    playerState: IPlayerState;
    restoredPlayerState: IPlayerState;
    entryPoint: EntryPoint;
}

export interface IContextRequest {
    context: ContextType;
    data?: any;
}

export interface IContextResponse {
    responseType: ResponseType;
    status: ResponseStatus;
    data?: any;
}

export const GameActions = {
    setLoadedData: createAction(
        Types.SET_LOADED_DATA,
        resolve => (data: IGameLoadData) => resolve<IGameLoadData>(data)
    ),

    gameLoaded: createAction(Types.GAME_LOADED),

    loadWorld: createAction(
        Types.LOAD_WORLD,
        resolve => (worldNumber: number, clearUI: boolean = true) =>
            resolve<ILoadWorldPayload>({
                worldNumber: worldNumber,
                clearUI: clearUI,
            })
    ),

    loadLevel: createAction(
        Types.LOAD_LEVEL,
        resolve => (
            levelNumber: number,
            levelPath: string,
            levelType: LevelType = LevelType.SAGA
        ) =>
            resolve<ILoadLevelPayload>({
                levelNumber,
                levelPath,
                levelType,
            })
    ),

    setSceneLoaded: createAction(Types.SET_SCENE_LOADED),

    setSceneLoading: createAction(Types.SET_SCENE_LOADING),

    endLevel: createAction(
        Types.END_LEVEL,
        resolve => (state: LevelStatus.RESTARTING | LevelStatus.EXITING) =>
            resolve(state)
    ),

    restartLevel: createAction(Types.RESTART_LEVEL),

    setContext: createAction(
        Types.SET_CONTEXT,
        resolve => (context: ContextType, data?: {}) =>
            resolve<IContextRequest>({ context: context, data: data })
    ),

    restoreContext: createAction(
        Types.RESTORE_DEFAULT_CONTEXT,
        resolve => (status: ResponseStatus, type: ResponseType, data?: {}) =>
            resolve<{
                status: ResponseStatus;
                type: ResponseType;
                data: {};
            }>({
                status: status,
                type: type,
                data: data,
            })
    ),

    enableIAP: createAction(
        Types.ENABLE_IAP,
        resolve => (unconsumedPurchases: ProductID[]) =>
            resolve<ProductID[]>(unconsumedPurchases)
    ),
    readyIAP: createAction(Types.READY_IAP),
};
